// src/store/modules/crm/actions.js
import defaultApi from "./api";

let api = defaultApi;

// FT-154: параметры доски — фильтры + серверные флаги unread/important (REQ-02).
// Один хелпер на первичную загрузку, догрузку, catch-up и counts_only, чтобы скоуп совпадал.
function boardParams(state, getters) {
  const pipeline = getters.currentPipeline;
  return {
    ...state.filters,
    pipeline_id: pipeline ? pipeline.id : null,
    unread: state.unreadOnly || undefined,
    important: state.importantOnly || undefined,
  };
}
// Модульный latch single-flight для catch-up: два `finally` двух колонок дают один запрос сейчас
// и ровно один повтор после (второй connected — в catchUpPending). Дренаж pending — только
// после снятия latch, иначе повтор отвергается и флаг зависает. Хелпер, не мутация: мутации
// не диспатчат, вызов живёт в finally экшенов.
let catchUpInFlight = false;
function drainPendingCatchUp({ state, dispatch }) {
  if (state && state.catchUpPending && dispatch) return dispatch("catchUpBoard");
  return undefined;
}
// Выборка доски — то, от чего зависит ответ leads/board (см. boardParams). Её меняют и без
// перезагрузки (applyUrlState на $route.query, alignPipeline дровера), поэтому сверяется сама
// выборка, а не только поколение; коммит тех же значений ответ не отбрасывает.
function boardSelection(state) {
  return JSON.stringify([state.currentPipelineId, state.filters, state.unreadOnly, state.importantOnly]);
}
// Снимок доски на старте запроса. superseded — началась более поздняя полная загрузка (она
// владеет kanbanLoading и бакетами); stale — ответ к доске уже не относится: доска
// перезагружалась или сменилась выборка.
function boardSnapshot(state) {
  const generation = state.boardGeneration;
  const selection = boardSelection(state);
  const superseded = () => generation !== state.boardGeneration;
  return { superseded, stale: () => superseded() || selection !== boardSelection(state) };
}
let countsTimer = null;
let countsDebounceMs = 1000;

const actions = {
  __setApi(mock) { api = mock; }, // test seam; no-op in prod
  __setCountsDebounce(ms) { countsDebounceMs = ms; }, // test seam

  async fetchReferenceData({ commit, state }) {
    if (state.referenceLoaded || state.referenceLoading) return;
    commit("SET_REFERENCE_LOADING", true);
    try {
      const [pipes, sources, users] = await Promise.all([
        api.fetchPipelines(),
        api.fetchLeadSources(),
        api.fetchUsers().catch(() => ({ items: [] })), // graceful 403 (EDGE-15)
      ]);
      const usersById = {};
      (users.items || []).forEach((u) => { usersById[u.id] = u; });
      commit("SET_REFERENCE", { pipelines: pipes.items, leadSources: sources.items, usersById });
    } catch (e) {
      commit("SET_REFERENCE_ERROR", e);
    }
  },

  // FT-156 REQ-08: лиды для меню на карточке брони — лид этой брони ∪ лиды её клиента (бэк
  // объединяет только при обоих параметрах; без клиента — только по брони). В state ничего не
  // пишет: результат живёт в компоненте меню. per_page 200 — потолок leads#index (предел REQ-08).
  fetchBookingLeads(_ctx, { bookingId, clientId } = {}) {
    const params = { booking_id: bookingId, per_page: 200 };
    if (clientId) params.client_id = clientId;
    return api.fetchLeads(params).then((r) => r.items);
  },
  async fetchLeadsList({ commit, state }, { page = 1, perPage = 50, sort = null, order = null } = {}) {
    commit("SET_LIST_LOADING", true);
    commit("SET_LIST_ERROR", null);
    try {
      const params = { ...state.filters, page, per_page: perPage };
      if (sort) { params.sort = sort; params.order = order || "asc"; }
      const { items, meta } = await api.fetchLeads(params);
      commit("SET_LIST", { items, meta });
    } catch (e) {
      commit("SET_LIST_ERROR", e);
    } finally {
      commit("SET_LIST_LOADING", false);
    }
  },

  // Инбокс непрочитанных (серверный, пагинируемый). page>1 — догрузка («показать ещё»).
  // archived НЕ прокидываем: инбокс — про активные непрочитанные.
  async fetchInbox({ commit, state, getters }, { page = 1, perPage = 30 } = {}) {
    if (page === 1) commit("SET_INBOX_LOADING", true);
    commit("SET_INBOX_ERROR", null);
    try {
      const pipeline = getters.currentPipeline;
      const params = {
        q: state.filters.q, source_id: state.filters.source_id, assigned_to_id: state.filters.assigned_to_id,
        pipeline_id: pipeline ? pipeline.id : null, page, per_page: perPage,
      };
      const { items, meta } = await api.fetchInbox(params);
      if (page > 1) commit("APPEND_INBOX", { items, meta }); else commit("SET_INBOX", { items, meta });
    } catch (e) {
      commit("SET_INBOX_ERROR", e);
    } finally {
      if (page === 1) commit("SET_INBOX_LOADING", false);
    }
  },

  // Один board-запрос вместо N (по запросу на колонку): первая страница (50) каждой стадии
  // воронки, бакеты комитим по всем стадиям (включая пустые). catchUp — тот же запрос на глубину
  // (perPage кратно 50, ≤ 200) под флагом catchingUp: доска не размонтирует колонки, scrollTop
  // и загруженное живут; дренаж pending при catch-up делает catchUpBoard после снятия latch.
  // Полная загрузка (не catch-up) — новое поколение доски: выборка та же, но догрузки и
  // counts_only прежней доски к ней не относятся. Catch-up поколения не открывает — он лишь
  // восстанавливает глубину той же доски, — поэтому обогнать загрузку может только загрузка, и
  // kanbanLoading обогнанной снимает поздняя. Устаревший ответ (boardSnapshot) бакеты и
  // kanbanError не трогает; свой catchingUp снимается всегда.
  async fetchLeadsForKanban({ commit, state, getters, dispatch }, { perPage = 50, catchUp = false } = {}) {
    const pipeline = getters.currentPipeline;
    if (!pipeline) return;
    if (!catchUp) commit("BUMP_BOARD_GENERATION");
    const { stale, superseded } = boardSnapshot(state);
    commit(catchUp ? "SET_CATCHING_UP" : "SET_KANBAN_LOADING", true);
    commit("SET_KANBAN_ERROR", null);
    try {
      const { stages } = await api.fetchBoard({ ...boardParams(state, getters), per_page: perPage });
      if (stale()) return;
      (pipeline.pipeline_stages || []).forEach((stage) => {
        const bucket = stages[stage.id] || stages[String(stage.id)] || { items: [], meta: {} };
        commit("SET_STAGE_LEADS", { stageId: stage.id, items: bucket.items, meta: bucket.meta });
      });
    } catch (e) {
      if (!stale()) commit("SET_KANBAN_ERROR", e);
    } finally {
      if (catchUp) commit("SET_CATCHING_UP", false);
      else if (!superseded()) {
        commit("SET_KANBAN_LOADING", false);
        await drainPendingCatchUp({ state, dispatch });
      }
    }
  },

  // Догрузка одной стадии (REQ-05/06): stage_id + page+1, всегда по 50. Синхронный guard —
  // observer на sentinel может эмитить повторно до того, как loadingMore доедет в пропсы.
  // Reject пробрасывается: тост показывает компонент (колонка и аккордеон), бакет не трогаем
  // (FM-03); 403 дополнительно кладём в kanbanError (FM-04).
  // Ответ и отказ догрузки, пережившей перезагрузку доски или смену выборки, отбрасываются
  // (boardSnapshot). loadingMore — по идентичности бакета, а не по
  // поколению: упавшая перезагрузка бакет не заменит, и флаг иначе залип бы навсегда.
  fetchMoreLeadsForStage({ commit, state, getters, dispatch }, stageId) {
    const b = state.leadsByStage[stageId];
    if (!b || b.loadingMore || !b.hasMore || state.kanbanLoading || state.catchingUp || state.catchUpPending) return undefined;
    const { stale } = boardSnapshot(state);
    commit("SET_STAGE_LOADING_MORE", { stageId, value: true });
    return api.fetchBoard({ ...boardParams(state, getters), stage_id: stageId, page: (b.page || 1) + 1, per_page: 50 })
      .then(({ stages }) => {
        if (stale()) return;
        const bucket = stages[stageId] || stages[String(stageId)] || { items: [], meta: {} };
        commit("APPEND_STAGE_LEADS", { stageId, items: bucket.items, meta: bucket.meta });
      })
      .catch((e) => {
        if (stale()) return;
        if (e && e.response && e.response.status === 403) commit("SET_KANBAN_ERROR", e);
        throw e;
      })
      .finally(() => {
        if (state.leadsByStage[stageId] === b) commit("SET_STAGE_LOADING_MORE", { stageId, value: false });
        return drainPendingCatchUp({ state, dispatch });
      });
  },

  // Мутации счётчиков (UPSERT_LEAD в неполный бакет, CLEAR_PENDING_MOVE) только помечают
  // countsStale — диспатчить они не могут. Эту проверку зовут экшены и обработчик cable
  // после коммитов: есть stale-бакет → один counts_only через debounce.
  refreshStaleCounts({ state, dispatch }) {
    const stale = Object.values(state.leadsByStage || {}).some((b) => b && b.countsStale);
    return stale ? dispatch("refreshStageCounts") : undefined;
  },

  // countsStale → один counts_only-запрос на всю доску за окно debounce: только meta,
  // SET_STAGE_COUNTS переписывает счётчики всех стадий (если доска с тех пор не перезагружалась).
  refreshStageCounts({ commit, state, getters }) {
    clearTimeout(countsTimer);
    countsTimer = setTimeout(() => {
      const { stale } = boardSnapshot(state);
      api.fetchBoard({ ...boardParams(state, getters), counts_only: true })
        .then(({ stages }) => { if (!stale()) commit("SET_STAGE_COUNTS", stages); })
        .catch(() => { /* счётчики освежатся следующим fetch/catch-up */ });
    }, countsDebounceMs);
  },

  // Catch-up по (ре)коннекту cable (REQ-03): восстановить загруженную глубину одним запросом.
  // Занято (первичная загрузка, идущий catch-up, догрузка любой колонки) → catchUpPending,
  // запуск один раз по завершении; latch снимается до дренажа pending.
  catchUpBoard({ commit, state, dispatch }) {
    const busy = state.kanbanLoading || state.catchingUp
      || Object.values(state.leadsByStage || {}).some((b) => b && b.loadingMore);
    if (busy || catchUpInFlight) { commit("SET_CATCH_UP_PENDING", true); return undefined; }
    catchUpInFlight = true;
    commit("SET_CATCH_UP_PENDING", false);
    const maxLoaded = Math.max(50, ...Object.values(state.leadsByStage || {}).map((b) => (b && b.items ? b.items.length : 0)));
    const perPage = Math.min(200, Math.ceil(maxLoaded / 50) * 50);
    return Promise.resolve(dispatch("fetchLeadsForKanban", { perPage, catchUp: true }))
      .finally(() => { catchUpInFlight = false; return drainPendingCatchUp({ state, dispatch }); });
  },

  async fetchLead({ commit }, id) {
    commit("SET_SELECTED_LOADING", true);
    commit("SET_SELECTED_ERROR", null);
    try {
      const { lead } = await api.fetchLead(id);
      commit("SET_SELECTED_LEAD", lead);
    } catch (e) {
      commit("SET_SELECTED_ERROR", e);
    } finally {
      commit("SET_SELECTED_LOADING", false);
    }
  },

  async refreshLead({ commit, state }, id) {
    const { lead } = await api.fetchLead(id);
    if (String(state.selectedLeadId) !== String(id)) return;
    commit("SET_SELECTED_LEAD", lead);
  },

  // Bundle: открыть карточку лида одним запросом — лид + первые страницы
  // задач/активностей/диалога. Заменяет каскад из 4 fetch при открытии drawer.
  async fetchLeadDetail({ commit }, id) {
    commit("SET_SELECTED_LOADING", true);
    commit("SET_SELECTED_ERROR", null);
    commit("SET_LEAD_MESSAGES_LOADING", true);
    commit("SET_LEAD_ACTIVITIES_LOADING", true);
    commit("SET_LEAD_TASKS_LOADING", true);
    try {
      const { lead, tasks, activities, messages, assistant } = await api.fetchLeadDetail(id);
      commit("SET_SELECTED_LEAD", lead);
      commit("SET_ASSISTANT_FLAG", assistant);
      commit("SET_LEAD_TASKS", tasks);
      commit("SET_LEAD_ACTIVITIES", activities);
      commit("SET_LEAD_MESSAGES", messages);
    } catch (e) {
      commit("SET_SELECTED_ERROR", e);
      commit("SET_LEAD_MESSAGES_ERROR", e);
      commit("SET_LEAD_ACTIVITIES_ERROR", e);
      commit("SET_LEAD_TASKS_ERROR", e);
    } finally {
      commit("SET_SELECTED_LOADING", false);
      commit("SET_LEAD_MESSAGES_LOADING", false);
      commit("SET_LEAD_ACTIVITIES_LOADING", false);
      commit("SET_LEAD_TASKS_LOADING", false);
    }
  },
};

function findLeadStage(state, id) {
  for (const stageId of Object.keys(state.leadsByStage || {})) {
    const bucket = state.leadsByStage[stageId];
    if (bucket && bucket.items.some((l) => l.id === id)) return Number(stageId);
  }
  return null;
}

actions.advanceLead = async function ({ commit, state, dispatch }, { id, stage_id, force }) {
  const fromStageId = findLeadStage(state, id);
  if (fromStageId === null) {
    const { lead } = await api.advanceLead(id, { stage_id, force });
    commit("UPSERT_LEAD", lead);
    if (dispatch) dispatch("refreshStaleCounts");
    return lead;
  }
  commit("MOVE_LEAD_OPTIMISTIC", { lead_id: id, from: fromStageId, to: stage_id });
  commit("SET_PENDING_MOVE", { lead_id: id, from: fromStageId, to: stage_id });
  try {
    const { lead } = await api.advanceLead(id, { stage_id, force });
    commit("UPSERT_LEAD", lead);
    commit("CLEAR_PENDING_MOVE", id); // +1 целевой стадии или countsStale, если она неполная
    if (dispatch) dispatch("refreshStaleCounts");
    return lead;
  } catch (err) {
    // Откат — своей мутацией: обратный MOVE_LEAD_OPTIMISTIC портил бы счётчики обеих стадий.
    commit("ROLLBACK_PENDING_MOVE", { lead_id: id, from: fromStageId, to: stage_id });
    commit("CLEAR_PENDING_MOVE", id);
    throw err;
  }
};

actions.createLead = async function (_ctx, attrs) { const { lead } = await api.createLead(attrs); return lead; };
actions.updateLead = async function ({ commit }, { id, attrs }) { const { lead } = await api.updateLead(id, attrs); commit("UPSERT_LEAD", lead); return lead; };
actions.loseLead = async function ({ commit }, { id, reason }) { const { lead } = await api.loseLead(id, reason); commit("UPSERT_LEAD", lead); return lead; };
actions.winLead = async function ({ commit }, { id }) { const { lead } = await api.winLead(id); commit("UPSERT_LEAD", lead); return lead; };
actions.attachBooking = async function ({ commit }, { id, booking_id, replace = false }) { const { lead } = await api.attachBooking(id, booking_id, replace); commit("UPSERT_LEAD", lead); return lead; };
actions.detachBooking = async function ({ commit }, { id }) { const { lead } = await api.detachBooking(id); commit("UPSERT_LEAD", lead); return lead; };
// Список для диалога выбора — в стор не кладём: он живёт ровно на время открытого диалога.
actions.bookingOptions = async function (_ctx, { id, q }) { return api.bookingOptions(id, q); };
actions.convertLead = async function ({ commit }, { id, car_id, start_date, end_date, booking }) {
  const { lead, booking: createdBooking } = await api.convertLead(id, { car_id, start_date, end_date, booking });
  if (lead) commit("UPSERT_LEAD", lead);
  return { lead, booking: createdBooking };
};
actions.convertToClient = async function ({ commit }, { id, ...attrs }) {
  const { lead } = await api.convertToClient(id, attrs);
  commit("UPSERT_LEAD", lead);
  return lead;
};
actions.archiveLead = async function ({ commit }, id) { const { lead } = await api.archiveLead(id); commit("UPSERT_LEAD", lead); return lead; };
actions.unarchiveLead = async function ({ commit }, id) { const { lead } = await api.unarchiveLead(id); commit("UPSERT_LEAD", lead); return lead; };
actions.updateUserAccess = async function (_ctx, { id, enabled }) { return api.updateUserAccess(id, enabled); };
actions.fetchFreeCars = function (_ctx, params) { return api.fetchFreeCars(params); };

// FT-059 pipeline/stage editor
actions.createPipeline = async function ({ commit }, attrs) { const { pipeline } = await api.createPipeline(attrs); commit("UPSERT_PIPELINE", pipeline); return pipeline; };
actions.updatePipeline = async function ({ commit }, { id, attrs }) { const { pipeline } = await api.updatePipeline(id, attrs); commit("UPSERT_PIPELINE", pipeline); return pipeline; };
actions.deletePipeline = async function ({ commit }, id) { await api.deletePipeline(id); commit("REMOVE_PIPELINE", id); return id; };
actions.createStage = async function ({ commit }, { pipelineId, attrs }) { const { pipeline } = await api.createStage(pipelineId, attrs); commit("UPSERT_PIPELINE", pipeline); return pipeline; };
actions.updateStage = async function ({ commit }, { pipelineId, stageId, attrs }) { const { pipeline } = await api.updateStage(pipelineId, stageId, attrs); commit("UPSERT_PIPELINE", pipeline); return pipeline; };
actions.deleteStage = async function ({ commit }, { pipelineId, stageId, targetId }) { const { pipeline } = await api.deleteStage(pipelineId, stageId, targetId); commit("UPSERT_PIPELINE", pipeline); return pipeline; };
actions.reorderStages = async function ({ commit }, { pipelineId, orderedIds }) { const { pipeline } = await api.reorderStages(pipelineId, orderedIds); commit("UPSERT_PIPELINE", pipeline); return pipeline; };

// FT-062 lead-sources editor
actions.createSource = async function ({ commit }, attrs) { const { sources } = await api.createSource(attrs); commit("SET_LEAD_SOURCES", sources); return sources; };
actions.updateSource = async function ({ commit }, { id, attrs }) { const { sources } = await api.updateSource(id, attrs); commit("SET_LEAD_SOURCES", sources); return sources; };
actions.deleteSource = async function ({ commit }, { id, targetId }) { const { sources } = await api.deleteSource(id, targetId); commit("SET_LEAD_SOURCES", sources); return sources; };
actions.reorderSources = async function ({ commit }, orderedIds) { const { sources } = await api.reorderSources(orderedIds); commit("SET_LEAD_SOURCES", sources); return sources; };

// FT-045b inbox
actions.fetchLeadMessages = async function ({ commit }, { leadId, before, limit }) {
  if (!before) commit("SET_LEAD_MESSAGES_LOADING", true);
  commit("SET_LEAD_MESSAGES_ERROR", null);
  try {
    const { items, identity, hasMore } = await api.fetchLeadMessages(leadId, { before, limit });
    if (before) commit("PREPEND_LEAD_MESSAGES", { items, hasMore });
    else commit("SET_LEAD_MESSAGES", { items, identity, hasMore });
  } catch (e) {
    commit("SET_LEAD_MESSAGES_ERROR", e);
    commit("SET_LEAD_MESSAGES_LOADING", false);
    throw e;
  }
};
actions.sendLeadReply = async function ({ commit }, { leadId, body }) {
  commit("SET_REPLY_SENDING", true);
  try {
    const { message } = await api.sendLeadReply(leadId, body);
    commit("APPEND_LEAD_MESSAGE", message);
    return message;
  } finally {
    commit("SET_REPLY_SENDING", false);
  }
};
// FT-171 ассистент CRM. latest кладём только если лид всё ещё тот же (быстрое переключение).
actions.fetchLatestSuggestion = async function ({ commit, state }, leadId) {
  const latest = await api.fetchLatestSuggestion(leadId);
  if (state.selectedLeadId !== null && String(state.selectedLeadId) !== String(leadId)) return null;
  commit("SET_ASSISTANT_LATEST", { leadId, latest });
  return latest;
};
actions.requestSuggestion = async function (_ctx, leadId) { return api.requestSuggestion(leadId); };
actions.markSuggestionShown = async function (_ctx, { leadId, sid }) { return api.markSuggestionShown(leadId, sid); };
// Отправка черновика: исходящее — в нитку, карточка гаснет (latest → null до события канала).
actions.sendSuggestion = async function ({ commit }, { leadId, sid, text }) {
  commit("SET_REPLY_SENDING", true);
  try {
    const { message, suggestion } = await api.sendSuggestion(leadId, sid, text);
    commit("APPEND_LEAD_MESSAGE", message);
    commit("SET_ASSISTANT_LATEST", { leadId, latest: null });
    return { message, suggestion };
  } finally {
    commit("SET_REPLY_SENDING", false);
  }
};
actions.rejectSuggestion = async function ({ commit }, { leadId, sid }) {
  const suggestion = await api.rejectSuggestion(leadId, sid);
  commit("SET_ASSISTANT_LATEST", { leadId, latest: null });
  return suggestion;
};
// Поля применены на сервере: обновляем лида без скелетона и текущие значения в latest.
actions.applySuggestionFields = async function ({ dispatch }, { leadId, sid, fields, expected }) {
  let applied;
  try {
    applied = await api.applySuggestionFields(leadId, sid, { fields, expected });
  } catch (err) {
    // 409 field_changed: текущие значения полей устарели — перечитываем latest, иначе каждое
    // следующее «Применить» снова уйдёт со старым expected и получит 409 (ревью круг 1, V4).
    if (err && err.response && err.response.status === 409) {
      await Promise.all([dispatch("refreshLead", leadId).catch(() => {}), dispatch("fetchLatestSuggestion", leadId).catch(() => {})]);
    }
    throw err;
  }
  await Promise.all([dispatch("refreshLead", leadId).catch(() => {}), dispatch("fetchLatestSuggestion", leadId).catch(() => {})]);
  return applied;
};
actions.fetchAssistantSettings = async function () { return api.fetchAssistantSettings(); };
actions.updateAssistantSettings = async function (_ctx, attrs) { return api.updateAssistantSettings(attrs); };
actions.sendLeadReplyFile = async function ({ commit }, { leadId, file, caption }) {
  commit("SET_REPLY_SENDING", true);
  try {
    const { message } = await api.sendLeadFile(leadId, file, caption);
    commit("APPEND_LEAD_MESSAGE", message);
    return message;
  } finally {
    commit("SET_REPLY_SENDING", false);
  }
};
actions.linkIdentityToClient = async function (_ctx, { identityId, clientId }) {
  return api.linkChannelIdentity(identityId, clientId);
};

// Activities (046a)
actions.fetchLeadActivities = async function ({ commit }, { leadId, page }) {
  if (!page || page === 1) commit("SET_LEAD_ACTIVITIES_LOADING", true);
  commit("SET_LEAD_ACTIVITIES_ERROR", null);
  try {
    const { items, meta } = await api.fetchLeadActivities(leadId, { page });
    if (page && page > 1) commit("APPEND_LEAD_ACTIVITIES", { items, meta });
    else commit("SET_LEAD_ACTIVITIES", { items, meta });
  } catch (e) {
    commit("SET_LEAD_ACTIVITIES_ERROR", e);
    commit("SET_LEAD_ACTIVITIES_LOADING", false);
    throw e;
  }
};

actions.createLeadActivity = async function ({ commit }, { leadId, activityType, body }) {
  commit("SET_ACTIVITIES_CREATING", true);
  try {
    const { activity } = await api.createLeadActivity(leadId, { activity_type: activityType, body });
    commit("UPSERT_LEAD_ACTIVITY", activity);
    return activity;
  } finally {
    commit("SET_ACTIVITIES_CREATING", false);
  }
};

actions.deleteLeadActivity = async function ({ commit }, { leadId, activityId }) {
  await api.deleteLeadActivity(leadId, activityId);
  commit("REMOVE_LEAD_ACTIVITY", activityId);
};

// Tasks (046b)
actions.fetchLeadTasks = async function ({ commit }, { leadId, page }) {
  if (!page || page === 1) commit("SET_LEAD_TASKS_LOADING", true);
  commit("SET_LEAD_TASKS_ERROR", null);
  try {
    const { items, meta } = await api.fetchLeadTasks(leadId, { page });
    if (page && page > 1) commit("APPEND_LEAD_TASKS", { items, meta }); else commit("SET_LEAD_TASKS", { items, meta });
  } catch (e) { commit("SET_LEAD_TASKS_ERROR", e); commit("SET_LEAD_TASKS_LOADING", false); throw e; }
};
actions.createLeadTask = async function ({ commit }, { leadId, ...payload }) {
  commit("SET_LEAD_TASKS_CREATING", true);
  try { const { task } = await api.createLeadTask(leadId, payload); commit("UPSERT_TASK", task); return task; }
  finally { commit("SET_LEAD_TASKS_CREATING", false); }
};
actions.updateLeadTask = async function ({ commit }, { leadId, taskId, ...payload }) {
  const { task } = await api.updateLeadTask(leadId, taskId, payload); commit("UPSERT_TASK", task); return task;
};
actions.completeLeadTask = async function ({ commit }, { leadId, taskId }) {
  const { task } = await api.completeLeadTask(leadId, taskId); commit("UPSERT_TASK", task); return task;
};
actions.reopenLeadTask = async function ({ commit }, { leadId, taskId }) {
  const { task } = await api.reopenLeadTask(leadId, taskId); commit("UPSERT_TASK", task); return task;
};
actions.stopRecurrence = async function ({ commit }, { leadId, taskId }) {
  const { task } = await api.stopRecurrence(leadId, taskId); commit("UPSERT_TASK", task); return task;
};
actions.deleteLeadTask = async function ({ commit }, { leadId, taskId }) {
  await api.deleteLeadTask(leadId, taskId); commit("REMOVE_LEAD_TASK", taskId);
};
actions.fetchMyTasks = async function ({ commit }, { assignee, status, page } = {}) {
  if (!page || page === 1) commit("SET_MY_TASKS_LOADING", true);
  commit("SET_MY_TASKS_ERROR", null);
  try {
    const { items, meta } = await api.fetchMyTasks({ assignee, status, page });
    if (page && page > 1) commit("APPEND_MY_TASKS", { items, meta }); else commit("SET_MY_TASKS", { items, meta });
  } catch (e) { commit("SET_MY_TASKS_ERROR", e); commit("SET_MY_TASKS_LOADING", false); throw e; }
};
let assignableUsersInflight = null;
actions.markLeadRead = async function ({ commit }, leadId) {
  commit("UPSERT_LEAD_UNREAD", { leadId, value: false }); // optimistic dot-off
  commit("REMOVE_INBOX_LEAD", leadId); // прочитан → уходит из инбокса непрочитанных
  try { await api.markLeadRead(leadId); }
  catch (e) { commit("UPSERT_LEAD_UNREAD", { leadId, value: true }); } // revert on error
};

actions.fetchAssignableUsers = async function ({ commit, state }) {
  if (state.assignableUsers.length) return;
  // Дедуп параллельных вызовов: на доске каждая kanban-карточка дёргает это в created —
  // без guard'а получили бы N одновременных запросов до резолва первого.
  if (assignableUsersInflight) return assignableUsersInflight;
  assignableUsersInflight = (async () => {
    try { const users = await api.fetchCrmUsers(); commit("SET_ASSIGNABLE_USERS", users); }
    catch (e) { /* graceful */ }
    finally { assignableUsersInflight = null; }
  })();
  return assignableUsersInflight;
};

// FT-093 REQ-06 (SC-08/CON-07): отметить тред прочитанным в Avito при его открытии.
// Fire-and-forget: вызывающий не ждёт результат и НИЧЕГО не показывает пользователю —
// сбой Avito (сеть или 200 { synced:false, code:"avito_read_failed" }) на UI не влияет.
// Канал проверяем здесь, а не в компоненте: identity приходит тем же bundle-ответом
// (fetchLeadDetail), а дровер канал-агностичен — не avito ⇒ тихий no-op без запроса.
actions.syncAvitoRead = async function ({ state }, leadId) {
  const identity = state.leadMessages && state.leadMessages.identity;
  if (!identity || identity.channel !== "avito") return null;
  try {
    return await api.syncAvitoRead(leadId);
  } catch (e) {
    console.warn("[crm] avito read-sync failed", e);
    return null;
  }
};

// FT-093 REQ-08: блокировка покупателя в Avito. Ошибки НЕ глушим — диалог показывает
// тост по коду ответа (admin_required / avito_* / 502).
actions.blacklistAvitoLead = async function (_ctx, { leadId, reasonId }) {
  return api.blacklistAvitoLead(leadId, reasonId);
};

function chatTenantGuard(rootGetters = {}) {
  const identity = () => `${(rootGetters.getMainCompany || {}).id || ''}:${rootGetters.getAuthToken || ''}`;
  const expected = identity();
  return () => { if (identity() !== expected) throw new Error('stale_chat_context'); };
}
actions.fetchWebsiteChat = async function ({ commit, rootGetters }, id) {
  const current = chatTenantGuard(rootGetters);
  const session = await api.fetchWebsiteChat(id);
  current();
  commit("SET_WEBSITE_CHAT", session);
  return session;
};
function websiteChatContextCurrent(state, sessionId, leadId, clientId) {
  const session = state.websiteChatSessions && state.websiteChatSessions[sessionId];
  if (!session || String(session.lead_id) !== String(leadId)) return false;
  const selected = state.selectedLead;
  if (!selected || String(selected.id) !== String(leadId)) return false;
  return clientId == null || String(selected.client_id) === String(clientId);
}
actions.createWebsiteChatDocumentRequest = async function ({ commit, state, rootGetters }, { sessionId, templateKey, clientRequestId }) {
  const current = chatTenantGuard(rootGetters);
  const session = state.websiteChatSessions && state.websiteChatSessions[sessionId];
  const leadId = session && session.lead_id;
  if (!websiteChatContextCurrent(state, sessionId, leadId)) throw new Error('stale_chat_context');
  const request = await api.createWebsiteChatDocumentRequest(sessionId, { template_key: templateKey, client_request_id: clientRequestId });
  current();
  if (websiteChatContextCurrent(state, sessionId, leadId)) commit("SET_WEBSITE_CHAT_DOCUMENT_REQUEST", { request, sessionId });
  return request;
};
actions.fetchWebsiteChatDocumentRequest = async function ({ commit, state, rootGetters }, { id, sessionId }) {
  const current = chatTenantGuard(rootGetters);
  const session = state.websiteChatSessions && state.websiteChatSessions[sessionId];
  const leadId = session && session.lead_id;
  if (!websiteChatContextCurrent(state, sessionId, leadId)) throw new Error('stale_chat_context');
  const request = await api.fetchWebsiteChatDocumentRequest(id);
  current();
  if (websiteChatContextCurrent(state, sessionId, leadId)) commit("SET_WEBSITE_CHAT_DOCUMENT_REQUEST", { request, sessionId });
  return request;
};
["cancel", "reject", "save"].forEach((operation) => {
  actions[`${operation}WebsiteChatDocumentRequest`] = async function ({ commit, state, rootGetters }, { id, sessionId, expectedClientId, ...payload }) {
    const current = chatTenantGuard(rootGetters);
    const session = state.websiteChatSessions && state.websiteChatSessions[sessionId];
    const leadId = session && session.lead_id;
    if (!websiteChatContextCurrent(state, sessionId, leadId, expectedClientId)) throw new Error('stale_chat_context');
    const request = await api[`${operation}WebsiteChatDocument${operation === "reject" || operation === "save" ? "Upload" : "Request"}`](id, payload);
    current();
    if (websiteChatContextCurrent(state, sessionId, leadId, expectedClientId)) commit("SET_WEBSITE_CHAT_DOCUMENT_REQUEST", { request, sessionId });
    return request;
  };
});
actions.downloadWebsiteChatDocumentUpload = async ({ rootGetters }, { id, uploadId }) => {
  const current = chatTenantGuard(rootGetters);
  const result = await api.downloadWebsiteChatDocumentUpload(id, uploadId);
  current(); return result;
};
["takeover", "resume", "close"].forEach((event) => {
  actions[`${event}WebsiteChat`] = async function ({ commit, rootGetters }, id) {
    const current = chatTenantGuard(rootGetters);
    const session = await api.transitionWebsiteChat(id, event);
    current();
    commit("SET_WEBSITE_CHAT", session);
    return session;
  };
});
actions.fetchChatWidgets = async function ({ commit, rootGetters }) {
  const current = chatTenantGuard(rootGetters);
  commit("SET_CHAT_WIDGETS", { widgets: [], companies: [], locations: [] });
  const result = await api.fetchChatWidgets();
  current();
  commit("SET_CHAT_WIDGETS", result);
  return result;
};
actions.fetchChatPreview = async function ({ rootGetters }, id) {
  const current = chatTenantGuard(rootGetters);
  const preview = await api.fetchChatPreview(id);
  current();
  return preview;
};
actions.startChatPreview = async function ({ rootGetters }, payload) {
  const current = chatTenantGuard(rootGetters);
  const session = await api.startChatPreview(payload);
  current();
  return session;
};
actions.saveChatWidget = async function ({ commit, rootGetters }, payload) {
  const current = chatTenantGuard(rootGetters);
  const widget = await api.saveChatWidget(payload);
  current();
  commit("UPSERT_CHAT_WIDGET", widget);
  return widget;
};
actions.rotateChatWidget = async function ({ commit, rootGetters }, id) {
  const current = chatTenantGuard(rootGetters);
  const widget = await api.rotateChatWidget(id);
  current();
  commit("UPSERT_CHAT_WIDGET", widget);
  return widget;
};

export default actions;
